// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { TREATMENTS, type Treatment } from '../../../api/types';
import { EAK_OPTIONS } from '../../../bygning/fields';
import { TREATMENT_LABEL } from '../../../kortlaegning/vocab';
import { Button } from '../../Button';
import { Select } from '../../Select';
import styles from './Tabel.module.css';

export interface BulkBarProps {
  /** Checked types. */
  count: number;
  /** Of those, the ones Godkend can approve (queued, no open sample). */
  approvable: number;
  canEdit: boolean;
  disabled: boolean;
  onTreatment: (t: Treatment) => void;
  onEak: (code: string) => void;
  onApprove: () => void;
  onClear: () => void;
}

/**
 * The bulk bar under Tabel › Typer (v8 `.bulk`): "N valgt · Sæt behandling ·
 * Sæt EAK · Godkend N · Ryd". The table sends each as one batch PATCH
 * (`PATCH /survey/types`, one transaction); an older server without the
 * batch route gets one PATCH per type (`CaseActions.patchTypes`).
 */
export function BulkBar({ count, approvable, canEdit, disabled, onTreatment, onEak, onApprove, onClear }: BulkBarProps) {
  return (
    <div className={styles.bulk} role="toolbar" aria-label="Valgte typer">
      <b className={styles.bulkCount}>{count} valgt</b>
      {canEdit && (
        <>
          <Select
            variant="filled"
            size="md"
            aria-label="Sæt behandling"
            value=""
            disabled={disabled}
            onChange={(e) => e.target.value && onTreatment(e.target.value as Treatment)}
          >
            <option value="">Sæt behandling…</option>
            {TREATMENTS.map((t) => (
              <option key={t} value={t}>
                {TREATMENT_LABEL[t]}
              </option>
            ))}
          </Select>
          <Select
            variant="filled"
            size="md"
            aria-label="Sæt EAK"
            value=""
            disabled={disabled}
            onChange={(e) => e.target.value && onEak(e.target.value)}
          >
            <option value="">Sæt EAK…</option>
            {EAK_OPTIONS.map((o) => (
              <option key={o.code} value={o.code}>
                {o.code} {o.name}
              </option>
            ))}
          </Select>
          <Button
            variant="primary"
            disabled={disabled || approvable === 0}
            title={approvable < count ? 'Typer der afventer prøvesvar eller allerede er godkendt springes over' : undefined}
            onClick={onApprove}
          >
            Godkend {approvable}
          </Button>
        </>
      )}
      <Button onClick={onClear}>Ryd</Button>
    </div>
  );
}
